// @boty/database - Database Query Utilities
// Common query functions for working with the Boty database

import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  Database,
  BotInsert,
  BotUpdate,
  BotContentInsert,
  MessageInsert,
  FeedbackInsert,
  BotSlugRedirectInsert,
  FeedbackSubmissionInsert,
  FeedbackSubmissionUpdate,
} from './types';

type DbClient = SupabaseClient<Database>;

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

function validateNonEmptyString(value: string, paramName: string): void {
  if (!value || typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`${paramName} must be a non-empty string`);
  }
}

function validateUUID(value: string, paramName: string): void {
  validateNonEmptyString(value, paramName);
  // Basic UUID format check (not exhaustive, but catches obvious errors)
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(value)) {
    throw new Error(`${paramName} must be a valid UUID`);
  }
}

// ============================================================================
// BOT QUERIES
// ============================================================================

/**
 * Get a bot by its unique slug
 */
export async function getBotBySlug(client: DbClient, slug: string) {
  validateNonEmptyString(slug, 'slug');

  const { data, error } = await client
    .from('bots')
    .select('*')
    .eq('slug', slug)
    .eq('is_active', true)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get a bot by its ID
 */
export async function getBotById(client: DbClient, id: string) {
  validateUUID(id, 'id');

  const { data, error } = await client
    .from('bots')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get all bots for a user
 */
export async function getUserBots(client: DbClient, userId: string) {
  validateUUID(userId, 'userId');

  const { data, error } = await client
    .from('bots')
    .select('*')
    .eq('user_id', userId)
    .eq('is_active', true)
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Get all bots for a session (temporary bots)
 */
export async function getSessionBots(client: DbClient, sessionId: string) {
  validateUUID(sessionId, 'sessionId');

  const { data, error } = await client
    .from('bots')
    .select('*')
    .eq('session_id', sessionId)
    .eq('is_active', true)
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Create a new bot
 */
export async function createBot(client: DbClient, bot: BotInsert) {
  const { data, error } = await client
    .from('bots')
    .insert(bot)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update a bot
 */
export async function updateBot(
  client: DbClient,
  id: string,
  updates: BotUpdate
) {
  const { data, error } = await client
    .from('bots')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Soft delete a bot (set is_active to false)
 */
export async function deactivateBot(client: DbClient, id: string) {
  const { data, error } = await client
    .from('bots')
    .update({ is_active: false })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Publish a bot
 */
export async function publishBot(client: DbClient, id: string) {
  return updateBot(client, id, { status: 'published' });
}

/**
 * Convert a temporary bot to a user's bot
 */
export async function claimBot(
  client: DbClient,
  botId: string,
  userId: string
) {
  const { data, error } = await client
    .from('bots')
    .update({
      user_id: userId,
      session_id: null, // Clear session ownership
    })
    .eq('id', botId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ============================================================================
// BOT CONTENT QUERIES
// ============================================================================

/**
 * Get all content for a bot, ordered by display_order
 */
export async function getBotContent(client: DbClient, botId: string) {
  const { data, error } = await client
    .from('bot_content')
    .select('*')
    .eq('bot_id', botId)
    .order('display_order', { ascending: true });

  if (error) throw error;
  return data;
}

/**
 * Estimate token count from content string.
 * Uses ~4 characters per token heuristic (common for English/mixed text).
 */
export function estimateTokenCount(content: string): number {
  return Math.ceil(content.length / 4);
}

/**
 * Add content to a bot.
 * Automatically estimates token_count from content if not provided.
 */
export async function addBotContent(
  client: DbClient,
  content: BotContentInsert
) {
  const withTokenCount = {
    ...content,
    token_count: content.token_count ?? estimateTokenCount(content.content),
  };

  const { data, error } = await client
    .from('bot_content')
    .insert(withTokenCount)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update bot content.
 * Automatically recalculates token_count when content changes.
 */
export async function updateBotContent(
  client: DbClient,
  contentId: string,
  updates: { content: string }
) {
  validateUUID(contentId, 'contentId');

  const { data, error } = await client
    .from('bot_content')
    .update({
      content: updates.content,
      token_count: estimateTokenCount(updates.content),
    })
    .eq('id', contentId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Delete bot content
 */
export async function deleteBotContent(client: DbClient, contentId: string) {
  const { error } = await client
    .from('bot_content')
    .delete()
    .eq('id', contentId);

  if (error) throw error;
}

/**
 * Reorder bot content by updating display_order for each item
 * @param orderedIds - Array of content IDs in the desired order
 */
export async function reorderBotContent(
  client: DbClient,
  botId: string,
  orderedIds: string[]
) {
  // Update each item's display_order based on its position in the array
  const updates = orderedIds.map((id, index) =>
    client
      .from('bot_content')
      .update({ display_order: index })
      .eq('id', id)
      .eq('bot_id', botId)
  );

  // Execute all updates
  const results = await Promise.all(updates);

  // Check for any errors
  const firstError = results.find((r) => r.error)?.error;
  if (firstError) {
    throw firstError;
  }

  return { count: orderedIds.length };
}

/**
 * Get the next display_order value for a bot's content
 */
export async function getNextDisplayOrder(
  client: DbClient,
  botId: string
): Promise<number> {
  const { data, error } = await client
    .from('bot_content')
    .select('display_order')
    .eq('bot_id', botId)
    .order('display_order', { ascending: false })
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') {
    // PGRST116 = no rows found, which is fine
    throw error;
  }

  return data ? data.display_order + 1 : 0;
}

/**
 * Get total token count for a bot's content
 */
export async function getBotTokenCount(client: DbClient, botId: string) {
  const { data, error } = await client
    .from('bot_content')
    .select('token_count')
    .eq('bot_id', botId);

  if (error) throw error;

  return data.reduce((sum, item) => sum + (item.token_count || 0), 0);
}

// ============================================================================
// MESSAGE QUERIES
// ============================================================================

/**
 * Get chat history for a bot session
 */
export async function getChatHistory(
  client: DbClient,
  botId: string,
  sessionId: string,
  limit = 50
) {
  const { data, error } = await client
    .from('messages')
    .select('*')
    .eq('bot_id', botId)
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true })
    .limit(limit);

  if (error) throw error;
  return data;
}

/**
 * Add a message to chat history.
 * Automatically estimates token_count from content if not provided.
 */
export async function addMessage(client: DbClient, message: MessageInsert) {
  const withTokenCount = {
    ...message,
    token_count: message.token_count ?? estimateTokenCount(message.content),
  };

  const { data, error } = await client
    .from('messages')
    .insert(withTokenCount)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get recent messages for a bot (for analytics)
 */
export async function getRecentMessages(
  client: DbClient,
  botId: string,
  limit = 100
) {
  const { data, error } = await client
    .from('messages')
    .select('*')
    .eq('bot_id', botId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data;
}

// ============================================================================
// FEEDBACK QUERIES
// ============================================================================

/**
 * Add feedback to a message
 */
export async function addFeedback(client: DbClient, feedback: FeedbackInsert) {
  const { data, error } = await client
    .from('feedback')
    .insert(feedback)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get feedback for a bot
 */
export async function getBotFeedback(client: DbClient, botId: string) {
  const { data, error } = await client
    .from('feedback')
    .select('*')
    .eq('bot_id', botId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Get feedback statistics for a bot
 */
export async function getBotFeedbackStats(client: DbClient, botId: string) {
  const { data, error } = await client
    .from('feedback')
    .select('rating')
    .eq('bot_id', botId);

  if (error) throw error;

  const positive = data.filter((f) => f.rating === 'positive').length;
  const negative = data.filter((f) => f.rating === 'negative').length;
  const total = positive + negative;

  return {
    positive,
    negative,
    total,
    positiveRate: total > 0 ? positive / total : 0,
  };
}

// ============================================================================
// SESSION UTILITIES
// ============================================================================

/**
 * Generate a cryptographically secure session ID
 */
export function generateSessionId(): string {
  return crypto.randomUUID();
}

/**
 * Check if a session owns a specific bot
 */
export async function sessionOwnBot(
  client: DbClient,
  sessionId: string,
  botId: string
): Promise<boolean> {
  const { data, error } = await client
    .from('bots')
    .select('id')
    .eq('id', botId)
    .eq('session_id', sessionId)
    .single();

  if (error) return false;
  return !!data;
}

// ============================================================================
// SLUG REDIRECT QUERIES
// ============================================================================

/**
 * Create a slug redirect record when a bot's slug changes
 */
export async function createSlugRedirect(
  client: DbClient,
  redirect: BotSlugRedirectInsert
) {
  const { data, error } = await client
    .from('bot_slug_redirects')
    .insert(redirect)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Find the current bot slug for an old slug (redirect lookup)
 * Returns the bot with its current slug if a redirect exists, null otherwise
 */
export async function findBotByOldSlug(client: DbClient, oldSlug: string) {
  validateNonEmptyString(oldSlug, 'oldSlug');

  // Find the redirect record
  const { data: redirect, error: redirectError } = await client
    .from('bot_slug_redirects')
    .select('bot_id')
    .eq('old_slug', oldSlug)
    .limit(1)
    .single();

  if (redirectError || !redirect) return null;

  // Get the current bot with its current slug
  const { data: bot, error: botError } = await client
    .from('bots')
    .select('id, slug, is_active, status')
    .eq('id', redirect.bot_id)
    .single();

  if (botError) return null;

  return bot;
}

/**
 * Get all slug redirects for a bot (for debugging/admin)
 */
export async function getBotSlugRedirects(client: DbClient, botId: string) {
  validateUUID(botId, 'botId');

  const { data, error } = await client
    .from('bot_slug_redirects')
    .select('*')
    .eq('bot_id', botId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

// ============================================================================
// SEO QUERIES
// ============================================================================

/**
 * Get all published, active, public bot slugs for sitemap generation.
 * Returns slug and updated_at for efficient sitemap building.
 * (Story 4.7: SEO-Optimized Bot Landing Page)
 */
export async function getPublishedBotSlugs(client: DbClient) {
  const { data, error } = await client
    .from('bots')
    .select('slug, updated_at')
    .eq('status', 'published')
    .eq('is_active', true)
    .eq('visibility', 'public');

  if (error) {
    console.error('[Database] getPublishedBotSlugs failed:', error);
    throw error;
  }

  return data || [];
}

/**
 * Get sample/demo bots for the samples page and landing page.
 * Uses is_sample flag with slug-based fallback for compatibility.
 */
// Grouped by the bot's actual primary_language (see the seed migrations).
// NOTE: techup-tel-aviv-2026 and david-chen-music are ENGLISH despite their
// Israeli subject matter — keep them under English.
const DEMO_BOT_SLUGS = [
  // Hebrew demos
  'taekwondo-championship-2026',
  'avi-repair-services',
  'cafe-shaked',
  'david-chen-music-he',
  // English demos
  'techup-tel-aviv-2026',
  'david-chen-music',
  'zen-flow-yoga',
  'emma-jake-wedding-2026',
  'ux-design-course-spring-2026',
  '123-oak-avenue-open-house',
  'green-streets-community-garden',
  // Spanish demos (clones of English ones with localized name/welcome)
  'zen-flow-yoga-es',
  'emma-jake-wedding-2026-es',
  'ux-design-course-spring-2026-es',
  '123-oak-avenue-open-house-es',
  'green-streets-community-garden-es',
  'david-chen-music-es',
];

export async function getSampleBots(client: DbClient, language?: string) {
  let query = client
    .from('bots')
    .select('slug, name, description, primary_language')
    .in('slug', DEMO_BOT_SLUGS)
    .eq('status', 'published')
    .eq('is_active', true);

  if (language) {
    query = query.eq('primary_language', language);
  }

  const { data, error } = await query.order('name');

  if (error) {
    console.error('[Database] getSampleBots failed:', error);
    throw error;
  }

  return data || [];
}

// ============================================================================
// FEEDBACK SUBMISSION QUERIES (Story 16.4)
// ============================================================================

/**
 * Add a feedback submission
 */
export async function addFeedbackSubmission(
  client: DbClient,
  submission: FeedbackSubmissionInsert
) {
  const { data, error } = await client
    .from('feedback_submissions')
    .insert(submission)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get feedback submissions with optional filters
 */
export async function getFeedbackSubmissions(
  client: DbClient,
  filters?: {
    type?: 'bug' | 'feature_request' | 'question' | 'other';
    status?: 'new' | 'reviewed' | 'resolved';
    limit?: number;
    offset?: number;
  }
) {
  let query = client
    .from('feedback_submissions')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false });

  if (filters?.type) {
    query = query.eq('type', filters.type);
  }
  if (filters?.status) {
    query = query.eq('status', filters.status);
  }
  if (filters?.limit) {
    query = query.limit(filters.limit);
  }
  if (filters?.offset) {
    query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1);
  }

  const { data, error, count } = await query;

  if (error) throw error;
  return { data: data || [], count: count || 0 };
}

/**
 * Update a feedback submission (e.g. change status)
 */
export async function updateFeedbackSubmission(
  client: DbClient,
  id: string,
  updates: FeedbackSubmissionUpdate
) {
  validateUUID(id, 'id');

  const { data, error } = await client
    .from('feedback_submissions')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Count recent submissions for rate limiting
 */
export async function countRecentSubmissions(
  client: DbClient,
  identifier: { userId?: string | undefined; email?: string | undefined },
  windowMs: number = 24 * 60 * 60 * 1000
) {
  const since = new Date(Date.now() - windowMs).toISOString();

  let query = client
    .from('feedback_submissions')
    .select('id', { count: 'exact', head: true })
    .gte('created_at', since);

  if (identifier.userId) {
    query = query.eq('user_id', identifier.userId);
  } else if (identifier.email) {
    query = query.eq('email', identifier.email);
  } else {
    return 0;
  }

  const { count, error } = await query;

  if (error) throw error;
  return count || 0;
}

// ============================================================================
// SUGGESTED QUESTIONS QUERIES (Story 4.10)
// ============================================================================

/**
 * Update a bot's suggested questions
 */
export async function updateBotSuggestedQuestions(
  client: DbClient,
  botId: string,
  questions: string[]
) {
  validateUUID(botId, 'botId');

  const { data, error } = await client
    .from('bots')
    .update({ suggested_questions: questions })
    .eq('id', botId)
    .select('id, suggested_questions')
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get a bot's stored suggested questions
 */
export async function getBotSuggestedQuestions(
  client: DbClient,
  botId: string
): Promise<string[] | null> {
  validateUUID(botId, 'botId');

  const { data, error } = await client
    .from('bots')
    .select('suggested_questions')
    .eq('id', botId)
    .single();

  if (error) throw error;
  return data?.suggested_questions as string[] | null;
}

/**
 * Toggle whether suggested questions are shown to consumers (Story 7.9).
 * The stored questions array is preserved either way.
 */
export async function setBotSuggestedQuestionsEnabled(
  client: DbClient,
  botId: string,
  enabled: boolean
) {
  validateUUID(botId, 'botId');

  const { data, error } = await client
    .from('bots')
    .update({ suggested_questions_enabled: enabled })
    .eq('id', botId)
    .select('id, suggested_questions_enabled')
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get top frequently asked questions for a bot from real user messages.
 * Uses server-side aggregation via RPC for performance (GROUP BY in SQL).
 * Falls back to client-side aggregation if the RPC is not yet deployed.
 */
export async function getTopQuestions(
  client: DbClient,
  botId: string,
  limit = 3,
  minOccurrences = 2
): Promise<string[]> {
  validateUUID(botId, 'botId');

  // Try server-side aggregation via RPC (efficient GROUP BY in SQL)
  const { data: rpcData, error: rpcError } = await client.rpc('get_top_questions', {
    p_bot_id: botId,
    p_limit: limit,
    p_min_occurrences: minOccurrences,
  });

  if (!rpcError && rpcData) {
    return (rpcData as Array<{ content: string; frequency: number }>).map((r) => r.content);
  }

  // Fallback: client-side aggregation with capped fetch (for environments where RPC not deployed)
  const { data, error } = await client
    .from('messages')
    .select('content')
    .eq('bot_id', botId)
    .eq('role', 'user')
    .eq('channel', 'web')
    .limit(1000);

  if (error) throw error;
  if (!data || data.length === 0) return [];

  const counts = new Map<string, number>();
  for (const msg of data) {
    const content = msg.content.trim();
    if (content.length > 0 && content.length <= 200) {
      counts.set(content, (counts.get(content) || 0) + 1);
    }
  }

  return Array.from(counts.entries())
    .filter(([, count]) => count >= minOccurrences)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([question]) => question);
}
